<template>
  <template v-if="variant === 'list' || windowWidth < 1200" v-for="prodotto of wizard.getProdotti(prodotti)" :key="prodotto.codice">
    <pf-radio
      :name="name"
      :required="required"
      :value="prodotto.codice_wizard"
      :checked="modelValue === prodotto.codice_wizard"
      @change="change($event)"
      class="wizard-radio"
    >
      <template #label>
        {{ prodotto.nome }}

        <div v-if="askQt && modelValue === prodotto.codice_wizard" style="max-width: 200px">
          <label>
            {{ $t('Quantità:') }}
            <x-input
              type="number"
              name="qt"
              required
              :min="1"
              :append="um"
              :model-value="qt"
              @update:model-value="emit('update:qt', Number($event))"
              :disabled="fixedQt"
            />
          </label>
        </div>
        <wizard-price-tag :product="prodotto" :qt="qt" />
        <form-help-tooltip v-if="priceHelp" :text="priceHelp" />
      </template>
      <template #description v-if="showFeature">
        <product-details v-if="showFeature" :product="prodotto" style="max-width: 80ch" />
      </template>
    </pf-radio>
  </template>

  <pf-table v-else striped>
    <pf-thead>
      <pf-tr>
        <pf-th :screen-reader-text="$t('Risorse')" />
        <pf-th v-for="prodotto of productWithFeatures" :key="prodotto.codice" class="product-name">
          <pf-radio
            component="label"
            :name="name"
            :required="required"
            :value="prodotto.codice_wizard"
            :checked="modelValue === prodotto.codice_wizard"
            @change="change($event)"
          >
            <template #label>
              <pf-title h="3">{{ prodotto.nome }}</pf-title>
            </template>
          </pf-radio>
          <wizard-price-tag :product="prodotto" variant="table" />
          <form-help-tooltip v-if="priceHelp" :text="priceHelp" />
        </pf-th>
      </pf-tr>
    </pf-thead>
    <pf-tbody>
      <template v-for="(feature, key) of allFeatures">
        <pf-tr v-if="showRow(key)" :key="key">
          <pf-td style="text-align: left">
            <component :is="feature.icon" />
            {{ feature.label }}
            <form-help-tooltip v-if="feature.tooltip" :text="feature.tooltip" />
          </pf-td>
          <pf-td v-for="prodotto of productWithFeatures" :key="prodotto.codice">
            <product-feature :feature="key" :product="prodotto" />
          </pf-td>
        </pf-tr>
      </template>
    </pf-tbody>
  </pf-table>
</template>

<style lang="scss" scoped>
th > .pf-v6-c-radio {
  display: block;
}

:deep(.pf-v6-c-table__th),
:deep(.pf-v6-c-table__td) {
  text-align: center;
}
</style>

<script lang="ts">
interface WizardQuestionProdottiProps<P extends readonly string[], T = ExtractValueType<P>> {
  modelValue: T | '' | undefined;
  name: string;
  readonly prodotti: P;
  qt?: number;
  required?: boolean;
  showFeature?: boolean;
  askQt?: boolean;
  fixedQt?: boolean;
  priceHelp?: string;
  um?: string;
  variant?: 'table' | 'list';
}

export type ExtractValueType<P> = P extends readonly (infer U)[] ? U : never;
</script>

<script setup lang="ts" generic="P extends readonly string[]">
import { computed } from 'vue';
import { $t as _t } from '@/i18n';
import { useWizardQuestion } from '@/wizard';
import { type ProductFeatures } from '@/resources';
import WizardPriceTag from '../wizard-price-tag.vue';
import ProductDetails from '../../../components/product-details.vue';
import ProductFeature, { getAllProductFeatures } from '../../product-feature.vue';
import FormHelpTooltip from '@/components/FormHelpTooltip.vue';
import { useWindowSize } from '@vueuse/core';

type T = ExtractValueType<P>;

defineOptions({
  inheritAttrs: false,
});

const props = withDefaults(defineProps<WizardQuestionProdottiProps<P>>(), {
  qt: 1,
  variant: 'list',
});

const emit = defineEmits<{
  (name: 'update:modelValue', v: T): void;
  (name: 'update:qt', v: number): void;
}>();

const allFeatures = getAllProductFeatures();

const { wizard } = useWizardQuestion();
const productWithFeatures = computed(() => wizard.getProdotti(props.prodotti));

const { width: windowWidth } = useWindowSize();

function showRow(feature: keyof ProductFeatures) {
  return productWithFeatures.value.filter(p => p[feature] || p[feature] === 0).length;
}

function change(e: Event) {
  if (e.target instanceof HTMLInputElement) {
    emit('update:modelValue', e.target.value as T);
  }
}
</script>
